import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { RichEditor } from '../components/RichEditor'
import {
  Mail,
  Eye,
  Send,
  Save,
  RotateCcw,
  Loader2,
  Copy,
  X,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { apiGet, apiPut, apiPost } from '../lib/api'

interface TemplateListItem {
  id: string
  key: string
  name: string
  description: string
  audience: 'customer' | 'admin'
  subject: string
  isActive: boolean
  updatedAt: string
}

interface TemplateVariable {
  key: string
  label: string
  example: string
}

interface TemplateDetail {
  id: string
  key: string
  name: string
  description: string
  audience: 'customer' | 'admin'
  subject: string
  preheader: string
  bodyHtml: string
  ctaLabel: string
  ctaUrl: string
  variables: TemplateVariable[]
  isActive: boolean
  updatedAt: string
}

export function EmailTemplates() {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<string | null>(null)

  const { data, isLoading } = useQuery<{ data: TemplateListItem[] }>({
    queryKey: ['email-templates'],
    queryFn: () => apiGet('/api/admin/email-templates'),
  })

  return (
    <>
      <Topbar
        title="Plantillas de email"
        subtitle="Edita el HTML de los correos transaccionales — preview + envío de prueba"
      />
      <div className="grid grid-cols-12 gap-4 p-6">
        {/* Lista */}
        <aside className="col-span-12 lg:col-span-4 xl:col-span-3">
          <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
            <div className="border-b border-ink-200 bg-ink-50 px-4 py-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                {data?.data.length ?? '…'} plantillas
              </h2>
            </div>
            {isLoading && (
              <div className="grid place-items-center py-10 text-ink-500">
                <Loader2 className="size-5 animate-spin" />
              </div>
            )}
            {data && (
              <ul className="max-h-[70vh] overflow-y-auto">
                {data.data.map((t) => (
                  <li key={t.key}>
                    <button
                      onClick={() => setSelected(t.key)}
                      className={`flex w-full items-start gap-2 border-b border-ink-100 px-4 py-3 text-left transition-colors ${
                        selected === t.key ? 'bg-brand-50' : 'hover:bg-ink-50'
                      }`}
                    >
                      <div
                        className={`mt-0.5 grid size-7 shrink-0 place-items-center rounded-lg ${
                          t.audience === 'admin' ? 'bg-violet-100 text-violet-700' : 'bg-brand-100 text-brand-700'
                        }`}
                      >
                        <Mail className="size-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-ink-900">{t.name}</div>
                        <div className="truncate text-[11px] text-ink-500">{t.subject}</div>
                        <div className="mt-1 flex items-center gap-1">
                          <Badge variant={t.audience === 'admin' ? 'warning' : 'info'}>
                            {t.audience === 'admin' ? 'Admin' : 'Cliente'}
                          </Badge>
                          {!t.isActive && <Badge>Inactivo</Badge>}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Editor */}
        <main className="col-span-12 lg:col-span-8 xl:col-span-9">
          {selected ? (
            <TemplateEditor
              key={selected}
              templateKey={selected}
              onClose={() => setSelected(null)}
              onSaved={() => qc.invalidateQueries({ queryKey: ['email-templates'] })}
            />
          ) : (
            <div className="grid place-items-center rounded-2xl border-2 border-dashed border-ink-200 bg-white py-32 text-center">
              <div>
                <Mail className="mx-auto size-10 text-ink-300" />
                <p className="mt-3 text-sm text-ink-500">Selecciona una plantilla a la izquierda</p>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  )
}

function TemplateEditor({
  templateKey,
  onClose,
  onSaved,
}: {
  templateKey: string
  onClose: () => void
  onSaved: () => void
}) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<{ data: TemplateDetail }>({
    queryKey: ['email-template', templateKey],
    queryFn: () => apiGet(`/api/admin/email-templates/${templateKey}`),
  })

  type TplForm = {
    name: string
    subject: string
    preheader: string
    bodyHtml: string
    ctaLabel: string
    ctaUrl: string
    isActive: boolean
  }
  const [form, setForm] = useState<TplForm | null>(null)

  useEffect(() => {
    if (data?.data) {
      setForm({
        name: data.data.name,
        subject: data.data.subject,
        preheader: data.data.preheader,
        bodyHtml: data.data.bodyHtml,
        ctaLabel: data.data.ctaLabel,
        ctaUrl: data.data.ctaUrl,
        isActive: data.data.isActive,
      })
    }
  }, [data])

  const saveMut = useMutation({
    mutationFn: (payload: typeof form) =>
      apiPut(`/api/admin/email-templates/${templateKey}`, {
        name: payload!.name,
        subject: payload!.subject,
        preheader: payload!.preheader || null,
        body_html: payload!.bodyHtml,
        cta_label: payload!.ctaLabel || null,
        cta_url: payload!.ctaUrl || null,
        is_active: payload!.isActive,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['email-template', templateKey] })
      onSaved()
    },
  })

  const restoreMut = useMutation({
    mutationFn: () => apiPost(`/api/admin/email-templates/${templateKey}`, { action: 'restore' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-template', templateKey] }),
  })

  if (isLoading || !data || !form) {
    return (
      <div className="grid place-items-center rounded-2xl border border-ink-200 bg-white py-20 text-ink-500">
        <Loader2 className="size-6 animate-spin" />
      </div>
    )
  }

  const tpl = data.data

  function setField<K extends keyof TplForm>(key: K, value: TplForm[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  function insertVariable(varKey: string) {
    const tag = `{{${varKey}}}`
    // Con el editor WYSIWYG ya no tenemos cursor del textarea — agregamos al final.
    // El usuario puede moverlo dentro del editor manualmente.
    if (!form) return
    setField('bodyHtml', form.bodyHtml + tag)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between rounded-2xl border border-ink-200 bg-white p-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-lg font-bold text-ink-900">{tpl.name}</h2>
            <Badge variant={tpl.audience === 'admin' ? 'warning' : 'info'}>
              {tpl.audience === 'admin' ? 'Admin' : 'Cliente'}
            </Badge>
            <code className="rounded bg-ink-100 px-1.5 py-0.5 font-mono text-[10px] text-ink-700">{tpl.key}</code>
          </div>
          {tpl.description && <p className="mt-1 text-xs text-ink-500">{tpl.description}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => restoreMut.mutate()}
            disabled={restoreMut.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
            title="Restaurar al template original"
          >
            <RotateCcw className="size-3.5" /> Restaurar
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-ink-500 hover:bg-ink-100"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Form */}
        <div className="space-y-4 lg:col-span-3">
          <div className="rounded-2xl border border-ink-200 bg-white p-5">
            <Field label="Nombre interno">
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                className={inputCls}
                maxLength={120}
              />
            </Field>
            <Field label="Asunto del correo" hint={`${form.subject.length} / 200`}>
              <input
                type="text"
                value={form.subject}
                onChange={(e) => setField('subject', e.target.value)}
                className={inputCls}
                maxLength={200}
              />
            </Field>
            <Field
              label="Preheader (preview en bandeja)"
              hint="Texto corto que se ve junto al asunto en Gmail / Outlook"
            >
              <input
                type="text"
                value={form.preheader}
                onChange={(e) => setField('preheader', e.target.value)}
                className={inputCls}
                maxLength={200}
              />
            </Field>
            <Field label="HTML del cuerpo" hint="Usa {{variable}} para datos dinámicos">
              <RichEditor
                value={form.bodyHtml}
                onChange={(v) => setField('bodyHtml', v)}
                placeholder="Cuerpo del correo (HTML)…"
                minHeight={380}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Texto del botón (CTA)" hint="Opcional">
                <input
                  type="text"
                  value={form.ctaLabel}
                  onChange={(e) => setField('ctaLabel', e.target.value)}
                  className={inputCls}
                  maxLength={80}
                />
              </Field>
              <Field label="URL del botón" hint="Acepta {{variable}}">
                <input
                  type="text"
                  value={form.ctaUrl}
                  onChange={(e) => setField('ctaUrl', e.target.value)}
                  className={`${inputCls} font-mono text-xs`}
                  maxLength={255}
                />
              </Field>
            </div>
            <label className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setField('isActive', e.target.checked)}
                className="size-4 rounded border-ink-300"
              />
              <span className="text-sm text-ink-700">Plantilla activa (si se desactiva, no se envía)</span>
            </label>
          </div>

          {saveMut.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mr-1 inline size-4" />
              {(saveMut.error as Error).message}
            </div>
          )}
          {saveMut.isSuccess && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
              <CheckCircle2 className="mr-1 inline size-4" /> Guardado
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => saveMut.mutate(form)}
              disabled={saveMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saveMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              Guardar cambios
            </button>
          </div>
        </div>

        {/* Sidebar: Variables + Preview + Test */}
        <aside className="space-y-4 lg:col-span-2">
          <div className="rounded-2xl border border-ink-200 bg-white p-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-500">
              Variables disponibles
            </h3>
            <p className="mb-2 text-[11px] text-ink-500">Click para insertar en el cursor del HTML.</p>
            <ul className="space-y-1.5">
              {tpl.variables.map((v) => (
                <li key={v.key}>
                  <button
                    type="button"
                    onClick={() => insertVariable(v.key)}
                    className="flex w-full items-center justify-between gap-2 rounded-md border border-ink-200 bg-ink-50 px-2 py-1.5 text-left text-xs hover:bg-brand-50 hover:border-brand-300"
                  >
                    <div className="min-w-0 flex-1">
                      <code className="font-mono text-[10px] font-bold text-brand-700">{`{{${v.key}}}`}</code>
                      <div className="truncate text-[11px] text-ink-700">{v.label}</div>
                    </div>
                    <Copy className="size-3 text-ink-400" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <PreviewBox templateKey={templateKey} form={form} />
          <TestSender templateKey={templateKey} />
        </aside>
      </div>
    </div>
  )
}

function PreviewBox({
  templateKey,
  form,
}: {
  templateKey: string
  form: {
    subject: string
    preheader: string
    bodyHtml: string
    ctaLabel: string
    ctaUrl: string
  }
}) {
  const [open, setOpen] = useState(false)
  const previewMut = useMutation<{ subject: string; html: string }, Error>({
    mutationFn: () =>
      apiPost(`/api/admin/email-templates/${templateKey}/preview`, {
        subject: form.subject,
        preheader: form.preheader,
        bodyHtml: form.bodyHtml,
        ctaLabel: form.ctaLabel,
        ctaUrl: form.ctaUrl,
      }),
  })

  function refresh() {
    setOpen(true)
    previewMut.mutate()
  }

  const html = useMemo(() => previewMut.data?.html ?? '', [previewMut.data])

  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-500">Vista previa</h3>
        <button
          type="button"
          onClick={refresh}
          disabled={previewMut.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-ink-900 px-2 py-1 text-[11px] font-semibold text-white hover:bg-ink-800 disabled:opacity-50"
        >
          {previewMut.isPending ? <Loader2 className="size-3 animate-spin" /> : <Eye className="size-3" />}
          Renderizar
        </button>
      </div>
      <p className="mb-2 text-[11px] text-ink-500">
        Usa valores de ejemplo de las variables. El render incluye header/footer de Maz.
      </p>
      {open && previewMut.data && (
        <div className="space-y-1">
          <div className="rounded-md bg-ink-50 px-2 py-1.5 text-[11px]">
            <span className="font-semibold">Asunto:</span> {previewMut.data.subject}
          </div>
          <iframe
            srcDoc={html}
            sandbox=""
            className="h-96 w-full rounded-md border border-ink-200 bg-white"
            title="Preview"
          />
        </div>
      )}
    </div>
  )
}

function TestSender({ templateKey }: { templateKey: string }) {
  const [email, setEmail] = useState('')
  const testMut = useMutation<{ ok: true; provider?: string }, Error, { to: string }>({
    mutationFn: (vars) => apiPost(`/api/admin/email-templates/${templateKey}/test`, vars),
  })

  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-500">
        Enviar correo de prueba
      </h3>
      <p className="mb-2 text-[11px] text-ink-500">
        Manda el template (con datos de ejemplo) a un correo real para verificar cómo se ve.
      </p>
      <div className="space-y-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="tu@ejemplo.com"
          className={inputCls}
        />
        <button
          type="button"
          onClick={() => email && testMut.mutate({ to: email })}
          disabled={!email || testMut.isPending}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {testMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          Enviar prueba
        </button>
        {testMut.isError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-700">
            {(testMut.error as Error).message}
          </div>
        )}
        {testMut.isSuccess && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-700">
            ✓ Enviado (provider: {testMut.data.provider ?? '—'})
          </div>
        )}
      </div>
    </div>
  )
}

const inputCls =
  'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-brand-500'

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="mb-3 block">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-semibold text-ink-700">{label}</span>
        {hint && <span className="text-[10px] text-ink-500">{hint}</span>}
      </div>
      {children}
    </label>
  )
}
